import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { $countriesStore, $updateAddressStore } from '@lib/store';
import type { AddressData, AddressType } from '@shared/api';
import { BaseInput, Button, Modal, Select } from '@shared/widgets';
import { useTranslation } from 'react-i18next';

interface IForm {
  CountryCode?: string;
  City: string;
  Street1: string;
  Street2: string;
  PostalCode: string;
}
const DEFAULT_VALUES: IForm = { CountryCode: undefined, City: '', Street1: '', Street2: '', PostalCode: '' };

interface ChangeAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: AddressType;
  address: AddressData | null;
  onSubmit?: () => void;
}

export const ChangeAddressModal = (props: ChangeAddressModalProps) => {
  const countryStore = useUnit($countriesStore.store);

  const { t } = useTranslation();

  const { control, formState, handleSubmit, reset } = useForm<IForm>({
    defaultValues: {
      ...DEFAULT_VALUES,
      CountryCode: props.address?.countryCode,
      City: props.address?.city || '',
      Street1: props.address?.street1 || '',
      Street2: props.address?.street2 || '',
      PostalCode: props.address?.postalCode || '',
    },
  });

  useEffect(() => {
    $countriesStore.events.getCountries();
  }, []);

  const closeHandler = () => {
    props.onClose();
    reset({
      ...DEFAULT_VALUES,
      CountryCode: props.address?.countryCode,
      City: props.address?.city || '',
      Street1: props.address?.street1 || '',
      Street2: props.address?.street2 || '',
      PostalCode: props.address?.postalCode || '',
    });
  };
  const submitHandler = async (data: IForm) => {
    const response = await $updateAddressStore.events.updateAddress({ Type: props.type, ...data, CountryCode: data.CountryCode! });
    if (response.success) {
      props.onSubmit?.();
      closeHandler();
    }
  };
  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={`Change ${props.type} Address`} width='380px'>
      <form className='flex flex-col gap-2' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='CountryCode'
          control={control}
          render={({ field }) => (
            <Select
              enableSearch
              label='Country'
              items={countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode }))}
              nullValue={{ isEnable: false, text: 'Country' }}
              required
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name='City'
          control={control}
          rules={{ maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) } }}
          render={({ field }) => <BaseInput {...field} label='City' placeholder='City' required />}
        />
        <Controller
          name='Street1'
          control={control}
          rules={{ maxLength: { value: 200, message: t('errors.form.maxCharacters', { ns: 'common', max: 200 }) } }}
          render={({ field }) => <BaseInput {...field} label='Street 1' placeholder='Street 1' required />}
        />
        <Controller
          name='Street2'
          control={control}
          rules={{ maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) } }}
          render={({ field }) => <BaseInput {...field} label='Street 2' placeholder='Street 2' />}
        />
        <Controller
          name='PostalCode'
          control={control}
          rules={{ maxLength: { value: 20, message: t('errors.form.maxCharacters', { ns: 'common', max: 20 }) } }}
          render={({ field }) => <BaseInput {...field} label='Postal Code' placeholder='Postal Code' />}
        />
        <Button className='mt-6' color='primary' type='submit' disabled={formState.isSubmitting}>
          Submit
        </Button>
      </form>
    </Modal>
  );
};
